import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {App, Button, DatePicker, Drawer, InputNumber, Segmented, Select, Table, Tooltip} from 'antd';
import {
    BarChartOutlined,
    ExperimentOutlined,
    LineChartOutlined,
    ReloadOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';
import {useI18n} from '../../../i18n/useI18n';
import {backtestDcaPlan, getDcaMarketCandles, updateDcaUserConfig} from '../../../services/dcaService';
import LightweightCandlestickChart, {KLineBar} from '../../../components/charts/LightweightCandlestickChart';
import type {
    DcaBacktestResult,
    DcaCondition,
    DcaIndicatorSeries,
    DcaMarketCandle,
    DcaPlanDraft,
    DcaUserConfig,
} from '../../../types/dca';
import {DCA_TIMEFRAMES, defaultCondition, fmtTime, parseConditionsJson, validateCondition,} from './dcaConditionUtil';
import {buildHighlightSegments, buildIndicatorLegend, toChartIndicators} from './dcaIndicatorUtil';
import DcaIndicatorLegend from './DcaIndicatorLegend';
import {
    buildConditionSummary,
    clampRangeToTimeframe,
    currentValueOf,
    defaultRange,
    formatPercent,
    formatQuantity,
    formatSignedUsdt,
    formatUsdt,
    pnlPercentOf,
    snapTriggers,
    validateCalcInputs,
} from './dcaCalculatorUtil';
import DcaConditionEditor from './DcaConditionEditor';

interface Props {
    /** 用户可选品种（allowedSymbols） */
    symbols: string[];
    /** 用户 DCA 配置（金额/白名单） */
    config: DcaUserConfig | null;
    /** 计划卡片「测算」预填（nonce 变化触发） */
    prefill: { draft: DcaPlanDraft; nonce: number } | null;
    onRequestSettings: () => void;
    onRequestCreate: (draft: DcaPlanDraft) => void;
    /** 金额内联保存成功后通知父级刷新配置 */
    onConfigSaved?: () => void;
}

interface CalcParams {
    symbol: string;
    amount: number;
    timeframe: string;
    op: 'AND' | 'OR';
    conditions: DcaCondition[];
    lifecycle: 'ONCE' | 'LOOP';
    maxBuyCount: number;
    startMs: number;
    endMs: number;
}

/**
 * DCA 计算器卡片（参考 bitculator DCA 卡片视觉）
 * 工具栏（统计/设置/图表/重置）+ 统计区 + 价格走势图 + 设置行（条件融入）+
 * 计算（条件回测）+ 触发明细 + 按此配置创建计划。
 */
const DcaCalculatorCard: React.FC<Props> = ({
                                                symbols,
                                                config,
                                                prefill,
                                                onRequestSettings,
                                                onRequestCreate,
                                                onConfigSaved,
                                            }) => {
    const {t} = useI18n();
    const {message} = App.useApp();

    const [symbol, setSymbol] = useState<string | undefined>();
    const [amount, setAmount] = useState<number | null>(null);
    const [timeframe, setTimeframe] = useState('15m');
    const [monitorFrequency, setMonitorFrequency] = useState<number>(5);
    const [lifecycle, setLifecycle] = useState<'ONCE' | 'LOOP'>('ONCE');
    const [maxBuyCount, setMaxBuyCount] = useState<number>(10);
    const [op, setOp] = useState<'AND' | 'OR'>('AND');
    const [conditions, setConditions] = useState<DcaCondition[]>([defaultCondition('EMA_CROSS')]);
    const [range, setRange] = useState(() => defaultRange('15m'));

    const [conditionOpen, setConditionOpen] = useState(false);
    const [draftOp, setDraftOp] = useState<'AND' | 'OR'>('AND');
    const [draftConditions, setDraftConditions] = useState<DcaCondition[]>([]);

    const [result, setResult] = useState<DcaBacktestResult | null>(null);
    const [calculating, setCalculating] = useState(false);
    const [candles, setCandles] = useState<DcaMarketCandle[]>([]);
    const [candleIndicators, setCandleIndicators] = useState<Record<string, DcaIndicatorSeries[]> | null>(null);
    /** 用户点击图例隐藏的指标（visibleKey → true） */
    const [hiddenIndicators, setHiddenIndicators] = useState<Record<string, boolean>>({});
    const [chartLoading, setChartLoading] = useState(false);

    const cardRef = useRef<HTMLDivElement>(null);
    const statsRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<HTMLDivElement>(null);
    const lastPrefillNonce = useRef(0);
    const chartReqId = useRef(0);

    useEffect(() => {
        if (symbols.length > 0 && (null == symbol || !symbols.includes(symbol))) {
            setSymbol(symbols[0]);
        }
    }, [symbols, symbol]);

    useEffect(() => {
        if (null != config?.amountUsdt && config.amountUsdt > 0) {
            setAmount(config.amountUsdt);
        }
    }, [config?.amountUsdt]);

    const loadCandles = useCallback(async (params: {
        symbol: string;
        timeframe: string;
        startMs?: number;
        endMs?: number;
        conditionsJson?: string;
    }) => {
        const reqId = ++chartReqId.current;
        setChartLoading(true);
        try {
            const data = await getDcaMarketCandles(params);
            if (chartReqId.current === reqId) {
                setCandles(data?.candles ?? []);
                setCandleIndicators(data?.indicators ?? null);
            }
        } catch (e: any) {
            if (chartReqId.current === reqId) {
                setCandles([]);
                setCandleIndicators(null);
                message.error(e?.message ?? t('dca.calc.chartEmpty'));
            }
        } finally {
            if (chartReqId.current === reqId) {
                setChartLoading(false);
            }
        }
    }, [message, t]);

    // 条件组合 JSON（随条件变化更新，驱动默认视图的指标+高亮预览）
    const conditionsJson = useMemo(() => JSON.stringify({op, conditions}), [op, conditions]);

    // 默认视图：进入页面/切换品种、时间帧或条件时按当前选择加载最近K线，旧计算结果失效
    useEffect(() => {
        if (null == symbol || '' === symbol) {
            setCandles([]);
            setCandleIndicators(null);
            return;
        }
        setResult(null);
        void loadCandles({symbol, timeframe, conditionsJson});
        // 仅随品种/时间帧/条件变化触发；语言切换等不应清空结果
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbol, timeframe, conditionsJson]);

    const runCalculate = useCallback(async (params: CalcParams) => {
        const err = validateCalcInputs(params, t);
        if (null != err) {
            message.warning(err);
            return;
        }
        setCalculating(true);
        try {
            // 金额以内联输入为准：先落用户配置，保证后端按同一金额模拟
            if (params.amount !== config?.amountUsdt) {
                try {
                    await updateDcaUserConfig({amountUsdt: params.amount});
                    onConfigSaved?.();
                } catch (e: any) {
                    message.error(e?.message ?? t('dca.calc.amountSaveFail'));
                    return;
                }
            }
            const res = await backtestDcaPlan({
                conditionsJson: JSON.stringify({op: params.op, conditions: params.conditions}),
                symbol: params.symbol,
                timeframe: params.timeframe,
                startMs: params.startMs,
                endMs: params.endMs,
                lifecycle: params.lifecycle,
                maxBuyCount: 'LOOP' === params.lifecycle ? params.maxBuyCount : 1,
            });
            if (null == res) {
                message.error(t('dca.backtest.error'));
                return;
            }
            setResult(res);
            // 计算结果后按回测区间刷新K线（区间视图，含买入点标记 + 条件指标/高亮）
            await loadCandles({
                symbol: params.symbol,
                timeframe: params.timeframe,
                startMs: params.startMs,
                endMs: params.endMs,
                conditionsJson: JSON.stringify({op: params.op, conditions: params.conditions}),
            });
        } catch (e: any) {
            message.error(e?.message ?? t('dca.backtest.error'));
        } finally {
            setCalculating(false);
        }
    }, [config?.amountUsdt, loadCandles, message, onConfigSaved, t]);

    // 计划「测算」预填：应用草稿并自动计算（nonce 防重复）
    useEffect(() => {
        if (null == prefill || prefill.nonce === lastPrefillNonce.current) {
            return;
        }
        lastPrefillNonce.current = prefill.nonce;
        const draft = prefill.draft;
        const parsed = parseConditionsJson(draft.conditionsJson);
        const nextOp = null == parsed ? op : parsed.op;
        const nextConditions = null == parsed ? conditions : parsed.conditions;
        const nextRange = defaultRange(draft.timeframe);
        setSymbol(draft.symbol);
        setTimeframe(draft.timeframe);
        setMonitorFrequency(draft.monitorFrequency);
        setLifecycle(draft.lifecycle);
        setMaxBuyCount(draft.maxBuyCount);
        setOp(nextOp);
        setConditions(nextConditions);
        setRange(nextRange);
        cardRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
        if (null != amount && amount > 0) {
            void runCalculate({
                symbol: draft.symbol,
                amount,
                timeframe: draft.timeframe,
                op: nextOp,
                conditions: nextConditions,
                lifecycle: draft.lifecycle,
                maxBuyCount: draft.maxBuyCount,
                startMs: nextRange.startMs,
                endMs: nextRange.endMs,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefill]);

    const handleCalculate = () => {
        if (null == symbol) {
            message.warning(t('dca.form.symbolRequired'));
            return;
        }
        if (null == amount) {
            message.warning(t('dca.calc.amountRequired'));
            return;
        }
        void runCalculate({
            symbol,
            amount,
            timeframe,
            op,
            conditions,
            lifecycle,
            maxBuyCount,
            startMs: range.startMs,
            endMs: range.endMs,
        });
    };

    const persistAmount = () => {
        if (null == amount || !(amount > 0) || amount === config?.amountUsdt) {
            return;
        }
        void updateDcaUserConfig({amountUsdt: amount})
            .then(() => onConfigSaved?.())
            .catch((e: any) => message.error(e?.message ?? t('dca.calc.amountSaveFail')));
    };

    const handleTimeframeChange = (tf: string) => {
        setTimeframe(tf);
        setRange((prev) => clampRangeToTimeframe(prev.startMs, prev.endMs, tf));
    };

    const handleStartChange = (value: Dayjs | null) => {
        if (null == value) {
            return;
        }
        const startMs = value.startOf('day').valueOf();
        if (startMs >= range.endMs) {
            message.warning(t('dca.calc.rangeInvalid'));
            return;
        }
        setRange((prev) => clampRangeToTimeframe(startMs, prev.endMs, timeframe));
    };

    const handleEndChange = (value: Dayjs | null) => {
        if (null == value) {
            return;
        }
        const endMs = Math.min(value.endOf('day').valueOf(), Date.now());
        if (endMs <= range.startMs) {
            message.warning(t('dca.calc.rangeInvalid'));
            return;
        }
        setRange((prev) => clampRangeToTimeframe(prev.startMs, endMs, timeframe));
    };

    const openConditions = () => {
        setDraftOp(op);
        setDraftConditions(conditions);
        setConditionOpen(true);
    };

    const applyConditions = () => {
        if (0 === draftConditions.length) {
            message.warning(t('dca.form.conditionEmpty'));
            return;
        }
        for (const cond of draftConditions) {
            const err = validateCondition(cond, t);
            if (null != err) {
                message.warning(err);
                return;
            }
        }
        setOp(draftOp);
        setConditions(draftConditions);
        setConditionOpen(false);
    };

    const handleReset = () => {
        setSymbol(symbols[0]);
        setAmount(null != config?.amountUsdt ? config.amountUsdt : null);
        setTimeframe('15m');
        setMonitorFrequency(5);
        setLifecycle('ONCE');
        setMaxBuyCount(10);
        setOp('AND');
        setConditions([defaultCondition('EMA_CROSS')]);
        setRange(defaultRange('15m'));
        setResult(null);
        cardRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    const handleCreate = () => {
        if (null == symbol) {
            message.warning(t('dca.form.symbolRequired'));
            return;
        }
        onRequestCreate({
            symbol,
            timeframe,
            monitorFrequency,
            lifecycle,
            maxBuyCount: 'LOOP' === lifecycle ? maxBuyCount : 1,
            conditionsJson: JSON.stringify({op, conditions}),
        });
    };

    const currentValue = useMemo(() => currentValueOf(result), [result]);
    const pnlPercent = useMemo(() => pnlPercentOf(result), [result]);
    const markers = useMemo(
        () => snapTriggers(result?.triggers ?? [], candles),
        [result, candles],
    );

    // K线数据 → 统一图表组件格式
    const bars = useMemo<KLineBar[]>(() => candles.map((c) => ({
        time: c.time,
        open: Number(c.open),
        high: Number(c.high),
        low: Number(c.low),
        close: Number(c.close),
        volume: Number(c.volume ?? 0),
    })), [candles]);

    // 条件涉及指标序列 → 图表指标 + 可见性
    const chartIndicatorPayload = useMemo(
        () => toChartIndicators({candles, indicators: candleIndicators}),
        [candleIndicators],
    );

    // 图例项（条件涉及的技术指标标识 + 周期）
    const indicatorLegend = useMemo(() => buildIndicatorLegend(candleIndicators), [candleIndicators]);

    // 被隐藏的指标 key 集合（用于联动隐藏对应条件高亮）
    const hiddenIndicatorKeys = useMemo(
        () => new Set(Object.keys(hiddenIndicators).filter((k) => hiddenIndicators[k])),
        [hiddenIndicators],
    );

    // 图表最终可见性 = 基础可见性 ∩ 用户显隐
    const effectiveVisibleIndicators = useMemo(() => {
        const base = chartIndicatorPayload.visibleIndicators;
        const out: Record<string, boolean> = {};
        Object.keys(base).forEach((key) => {
            out[key] = !hiddenIndicators[key];
        });
        return out;
    }, [chartIndicatorPayload.visibleIndicators, hiddenIndicators]);

    const toggleIndicator = useCallback((visibleKey: string) => {
        setHiddenIndicators((prev) => ({...prev, [visibleKey]: !prev[visibleKey]}));
    }, []);

    // 命中段高亮（逐条件两线夹层；依赖指标被隐藏时同步隐藏）
    const highlightSegments = useMemo(
        () => buildHighlightSegments({op, conditions}, candles, candleIndicators, hiddenIndicatorKeys),
        [op, conditions, candles, candleIndicators, hiddenIndicatorKeys],
    );

    // 买入点标记 → 图表事件标记
    const eventMarkers = useMemo(() => markers.map((m) => ({
        time: m.time,
        position: 'belowBar' as const,
        color: '#22c55e',
        shape: 'circle' as const,
    })), [markers]);

    const triggerColumns = [
        {title: t('dca.backtest.list.time'), dataIndex: 'triggerTime', render: (v: number) => fmtTime(v)},
        {title: t('dca.backtest.list.price'), dataIndex: 'price', render: (v: number) => formatQuantity(v)},
        {title: t('dca.backtest.list.amount'), dataIndex: 'amountUsdt', render: (v: number) => formatUsdt(v)},
        {title: t('dca.backtest.list.quantity'), dataIndex: 'quantity', render: (v: number) => formatQuantity(v)},
    ];

    const renderCell = (label: string, content: React.ReactNode) => (
        <div className="dca-calc-cell" key={label}>
            <span className="dca-calc-cell-label">{label}</span>
            <div className="dca-calc-cell-value">{content}</div>
        </div>
    );

    const pnlClass = null != result?.unrealizedPnl ? (result.unrealizedPnl >= 0 ? 'dca-calc-pos' : 'dca-calc-neg') : '';

    return (
        <div className="dca-calc-card" ref={cardRef}>
            <div className="dca-calc-toolbar">
                <Tooltip title={t('dca.calc.stats')}>
                    <Button className="dca-calc-tool" icon={<BarChartOutlined/>}
                            onClick={() => statsRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'})}/>
                </Tooltip>
                <Tooltip title={t('dca.page.settings')}>
                    <Button className="dca-calc-tool" icon={<SettingOutlined/>} onClick={onRequestSettings}/>
                </Tooltip>
                <Tooltip title={t('dca.calc.chart')}>
                    <Button className="dca-calc-tool" icon={<LineChartOutlined/>}
                            onClick={() => chartRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'})}/>
                </Tooltip>
                <Tooltip title={t('dca.calc.reset')}>
                    <Button className="dca-calc-tool dca-calc-tool-end" icon={<ReloadOutlined/>}
                            onClick={handleReset}/>
                </Tooltip>
            </div>

            <div className="dca-calc-stats" ref={statsRef}>
                {null == result ? (
                    <div className="dca-calc-stats-empty">{t('dca.calc.noResult')}</div>
                ) : (
                    <>
                        <div className="dca-calc-stats-label">
                            {symbol} · {t('dca.calc.currentValue')}
                        </div>
                        <div className="dca-calc-stats-value">
                            <span className="dca-calc-stats-number">{formatUsdt(currentValue)}</span>
                            <span className={`dca-calc-stats-pct ${pnlClass}`}>{formatPercent(pnlPercent)}</span>
                        </div>
                        <div className="dca-calc-stats-meta">
                            <span>{t('dca.calc.invested')} <b>{formatUsdt(result.totalBuyAmount)}</b></span>
                            <span className="dca-calc-sep">·</span>
                            <span><b>{formatQuantity(result.totalBuyQuantity)}</b> {symbol}</span>
                            <span className="dca-calc-sep">·</span>
                            <span className={pnlClass}>
                                <b>{formatSignedUsdt(result.unrealizedPnl)}</b> P/L
                            </span>
                        </div>
                        <div className="dca-calc-stats-metrics">
                            <span>{t('dca.backtest.stats.triggers')} {result.totalTriggers}</span>
                            <span>{t('dca.backtest.stats.avgCost')} {formatQuantity(result.avgCost)}</span>
                            <span>{t('dca.backtest.stats.latestPrice')} {formatQuantity(result.latestPrice)}</span>
                            <span>{t('dca.backtest.stats.maxDrawdown')} {formatPercent(-Math.abs(Number(result.maxPriceDrawdownPct)))}</span>
                        </div>
                    </>
                )}
            </div>

            <div className="dca-calc-chart-wrap" ref={chartRef}>
                {0 === candles.length ? (
                    <div className="dca-calc-chart-empty">
                        {chartLoading ? t('dca.calc.chartLoading') : t('dca.calc.chartEmpty')}
                    </div>
                ) : (
                    <>
                        <DcaIndicatorLegend
                            items={indicatorLegend}
                            hidden={hiddenIndicators}
                            onToggle={toggleIndicator}
                        />
                        <LightweightCandlestickChart
                            data={bars}
                            indicators={chartIndicatorPayload.indicators}
                            visibleIndicators={effectiveVisibleIndicators}
                            highlightSegments={highlightSegments}
                            eventMarkers={eventMarkers}
                            height={300}
                            maxVisibleBars={240}
                        />
                        <div className="dca-calc-chart-legend">
                            {markers.length > 0 ? (
                                <>
                                    <span className="dca-calc-dot"/>{t('dca.calc.buyPoint')}
                                </>
                            ) : null}
                            {null != result ? (
                                <span className="dca-calc-chart-range">
                                    {fmtTime(result.startTime)} ~ {fmtTime(result.endTime)}
                                    {' · '}{t('dca.backtest.klineCount')} {result.klineCount}
                                </span>
                            ) : null}
                        </div>
                    </>
                )}
            </div>

            <div className="dca-calc-settings">
                {renderCell(t('dca.form.symbol'), (
                    <Select
                        value={symbol}
                        variant="borderless"
                        options={symbols.map((s) => ({label: s, value: s}))}
                        placeholder={t('dca.form.symbolEmpty')}
                        onChange={(v) => setSymbol(v)}
                    />
                ))}
                {renderCell(t('dca.calc.amount'), (
                    <InputNumber
                        value={amount}
                        variant="borderless"
                        min={0.01}
                        step={10}
                        onChange={(v) => setAmount(v ?? null)}
                        onBlur={persistAmount}
                    />
                ))}
                {renderCell(t('dca.form.timeframe'), (
                    <Select
                        value={timeframe}
                        variant="borderless"
                        options={DCA_TIMEFRAMES.map((tf) => ({label: tf, value: tf}))}
                        onChange={handleTimeframeChange}
                    />
                ))}
                {renderCell(t('dca.calc.frequency'), (
                    <InputNumber
                        value={monitorFrequency}
                        variant="borderless"
                        min={1}
                        max={1440}
                        onChange={(v) => setMonitorFrequency(v ?? 1)}
                    />
                ))}
                {renderCell(t('dca.calc.conditions'), (
                    <Button className="dca-calc-cond-btn" type="text" onClick={openConditions}>
                        {buildConditionSummary(op, conditions.length, t)}
                    </Button>
                ))}
                {renderCell(t('dca.form.lifecycle'), (
                    <Segmented
                        size="small"
                        value={lifecycle}
                        options={[
                            {label: t('dca.plan.lifecycle.ONCE'), value: 'ONCE'},
                            {label: t('dca.plan.lifecycle.LOOP'), value: 'LOOP'},
                        ]}
                        onChange={(v) => setLifecycle(v as 'ONCE' | 'LOOP')}
                    />
                ))}
                {'LOOP' === lifecycle ? renderCell(t('dca.calc.maxBuyCount'), (
                    <InputNumber
                        value={maxBuyCount}
                        variant="borderless"
                        min={1}
                        onChange={(v) => setMaxBuyCount(v ?? 1)}
                    />
                )) : null}
                {renderCell(t('dca.calc.start'), (
                    <DatePicker
                        value={dayjs(range.startMs)}
                        variant="borderless"
                        format="YYYY-MM-DD"
                        allowClear={false}
                        disabledDate={(d) => d.isAfter(dayjs(), 'day')}
                        onChange={handleStartChange}
                    />
                ))}
                {renderCell(t('dca.calc.end'), (
                    <DatePicker
                        value={dayjs(range.endMs)}
                        variant="borderless"
                        format="YYYY-MM-DD"
                        allowClear={false}
                        disabledDate={(d) => d.isAfter(dayjs(), 'day')}
                        onChange={handleEndChange}
                    />
                ))}
            </div>

            <div className="dca-calc-actions">
                <Button type="primary" shape="round" size="large" className="dca-calc-run"
                        loading={calculating} icon={<ExperimentOutlined/>} onClick={handleCalculate}>
                    {calculating ? t('dca.calc.calculating') : t('dca.calc.calculate')}
                </Button>
            </div>

            {null != result ? (
                <div className="dca-calc-result">
                    <div className="dca-calc-result-head">
                        <span className="dca-calc-result-title">{t('dca.backtest.list')}</span>
                        <Button type="link" onClick={handleCreate}>
                            {t('dca.calc.createFromResult')}
                        </Button>
                    </div>
                    {0 === result.triggers.length ? (
                        <div className="dca-calc-empty">{t('dca.backtest.empty')}</div>
                    ) : (
                        <Table
                            rowKey={(row) => String(row.triggerTime)}
                            size="small"
                            pagination={false}
                            dataSource={result.triggers}
                            columns={triggerColumns}
                        />
                    )}
                </div>
            ) : null}

            <Drawer
                open={conditionOpen}
                width={720}
                title={t('dca.calc.conditions')}
                onClose={() => setConditionOpen(false)}
                extra={
                    <Button type="primary" onClick={applyConditions}>
                        {t('dca.common.ok')}
                    </Button>
                }
            >
                <div className="dca-form-tip">{t('dca.form.conditionsTip')}</div>
                <DcaConditionEditor
                    conditions={draftConditions}
                    op={draftOp}
                    onChange={(nextOp, nextConditions) => {
                        setDraftOp(nextOp);
                        setDraftConditions(nextConditions);
                    }}
                />
            </Drawer>
        </div>
    );
};

export default DcaCalculatorCard;
